import React, { useState, useEffect } from "react";
import type { LeadStats } from "../hooks/useLeadStats";
import { ChevronDownIcon } from "@/components/icons";

interface LeadStatsBarProps {
  stats: LeadStats;
}

const STORAGE_KEY = "leadStatsBarCollapsed";

function TrendArrow({ change }: { change: number | null }) {
  if (change === null) {
    return <span className="text-gray-400 text-xs ml-1">-</span>;
  }
  if (change > 0) {
    return (
      <span className="text-green-600 text-xs ml-1 flex items-center">
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
        {change}%
      </span>
    );
  }
  if (change < 0) {
    return (
      <span className="text-red-600 text-xs ml-1 flex items-center">
        <ChevronDownIcon className="w-3 h-3" />
        {Math.abs(change)}%
      </span>
    );
  }
  return <span className="text-gray-400 text-xs ml-1">-</span>;
}

function StatItem({
  value,
  label,
  change,
  color,
}: {
  value: number;
  label: string;
  change?: number | null;
  color: string;
}) {
  return (
    <div className="flex items-center gap-1">
      <span className={`font-semibold ${color}`}>{value}</span>
      <span className="text-gray-500 text-xs">{label}</span>
      {change !== undefined && <TrendArrow change={change} />}
    </div>
  );
}

function StatGroup({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">
        {title}
      </span>
      <div className="flex flex-col gap-0.5">{children}</div>
    </div>
  );
}

export function LeadStatsBar({ stats }: LeadStatsBarProps) {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === "true";
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(isCollapsed));
  }, [isCollapsed]);

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="text-sm font-medium text-gray-700">Lead Stats</span>
        <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${
            isCollapsed ? "" : "rotate-180"
          }`} />
      </button>

      {!isCollapsed && (
        <div className="px-4 pb-3 pt-1 border-t border-gray-100">
          <div className="flex items-start gap-8">
            <StatGroup title="This Month">
              <StatItem
                value={stats.thisMonth.current.added}
                label="added"
                change={stats.thisMonth.changes.added}
                color="text-blue-600"
              />
              <StatItem
                value={stats.thisMonth.current.contacted}
                label="contacted"
                change={stats.thisMonth.changes.contacted}
                color="text-amber-600"
              />
              <StatItem
                value={stats.thisMonth.current.won}
                label="won"
                change={stats.thisMonth.changes.won}
                color="text-green-600"
              />
            </StatGroup>

            <div className="w-px h-12 bg-gray-200 self-center" />

            <StatGroup title="Last 2 Weeks">
              <StatItem
                value={stats.lastTwoWeeks.current.added}
                label="added"
                change={stats.lastTwoWeeks.changes.added}
                color="text-blue-600"
              />
              <StatItem
                value={stats.lastTwoWeeks.current.contacted}
                label="contacted"
                change={stats.lastTwoWeeks.changes.contacted}
                color="text-amber-600"
              />
              <StatItem
                value={stats.lastTwoWeeks.current.won}
                label="won"
                change={stats.lastTwoWeeks.changes.won}
                color="text-green-600"
              />
            </StatGroup>

            <div className="w-px h-12 bg-gray-200 self-center" />

            <StatGroup title="All Time">
              <StatItem
                value={stats.allTime.total}
                label="total leads"
                color="text-gray-700"
              />
              <StatItem
                value={stats.allTime.won}
                label="won"
                color="text-green-600"
              />
              <div className="flex items-center gap-1">
                <span className="font-semibold text-purple-600">
                  {stats.allTime.conversionRate}%
                </span>
                <span className="text-gray-500 text-xs">conversion</span>
              </div>
            </StatGroup>
          </div>
        </div>
      )}
    </div>
  );
}
